// src/layouts/AdminLayout.jsx
import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Space, Button, Badge, Grid } from 'antd';
import {
    DashboardOutlined,
    TeamOutlined,
    ShoppingOutlined,
    SafetyCertificateOutlined,
    BankOutlined,
    FileTextOutlined,
    LogoutOutlined,
    HomeOutlined,
    AppstoreOutlined,
    DollarOutlined,
    GlobalOutlined,
    SettingOutlined,
    TagsOutlined,
    MenuOutlined,
    NotificationOutlined,
    FileSearchOutlined,
    BellOutlined,
} from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { CloudDownloadOutlined } from '@ant-design/icons';

import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../constants/routes';

const { Sider, Content, Header } = Layout;
const { useBreakpoint } = Grid;



const MENU_ITEMS = [
    { key: ROUTES.ADMIN, icon: <DashboardOutlined />, label: 'Overview' },
    { key: ROUTES.ADMIN_USERS, icon: <TeamOutlined />, label: 'Users' },
    { key: ROUTES.ADMIN_BRANDS, icon: <TagsOutlined />, label: 'Brands' },
    { key: ROUTES.ADMIN_PRODUCTS, icon: <AppstoreOutlined />, label: 'Products' },
    { key: ROUTES.ADMIN_ORDERS, icon: <ShoppingOutlined />, label: 'Orders' },
    { key: ROUTES.ADMIN_KYC, icon: <SafetyCertificateOutlined />, label: 'KYC review' },
    { key: ROUTES.ADMIN_LIQUIDITY, icon: <BankOutlined />, label: 'Liquidity' },
    { key: ROUTES.ADMIN_ANNOUNCEMENTS, icon: <NotificationOutlined />, label: 'Announcements' },
    { key: ROUTES.ADMIN_TRANSLATIONS, icon: <GlobalOutlined />, label: 'Translations' },
    { key: ROUTES.ADMIN_REPORTS, icon: <FileTextOutlined />, label: 'Reports' },
    { key: ROUTES.ADMIN_COINS, icon: <DollarOutlined />, label: 'Coins' },
    { key: ROUTES.ADMIN_AUDIT, icon: <FileSearchOutlined />, label: 'Audit log' },
    { key: ROUTES.ADMIN_BACKUPS, icon: <CloudDownloadOutlined />, label: 'Backups' },
    { key: ROUTES.ADMIN_SETTINGS, icon: <SettingOutlined />, label: 'Settings' },
];

export default function AdminLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const { user, signOut } = useAuth();
    const [collapsed, setCollapsed] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);

    const userInitial = (user?.email || 'A').charAt(0).toUpperCase();

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate(ROUTES.LOGIN, { replace: true });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Sign out failed:', e);
        }
    };

    const userMenu = {
        items: [
            {
                key: 'home',
                icon: <HomeOutlined />,
                label: 'Back to app',
                onClick: () => navigate(ROUTES.DASHBOARD),
            },
            { type: 'divider' },
            {
                key: 'logout',
                icon: <LogoutOutlined />,
                label: 'Sign out',
                danger: true,
                onClick: handleSignOut,
            },
        ],
    };

    const menu = (
        <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={MENU_ITEMS.map((item) => ({
                key: item.key,
                icon: item.icon,
                label: <Link to={item.key}>{item.label}</Link>,
            }))}
            onClick={() => isMobile && setMobileOpen(false)}
        />
    );

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {!isMobile && (
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={setCollapsed}
                    theme="dark"
                    width={240}
                    collapsedWidth={72}
                    style={{
                        position: 'fixed',
                        top: 0,
                        insetInlineStart: 0,
                        height: '100vh',
                        overflow: 'auto',
                        zIndex: 100,
                    }}
                >
                    <div
                        style={{
                            height: 56,
                            margin: '16px 16px 8px',
                            color: '#fff',
                            fontWeight: 800,
                            fontSize: 18,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            gap: 10,
                            letterSpacing: 1,
                        }}
                    >
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                background: 'linear-gradient(135deg, #1677ff, #4096ff)',
                                display: 'grid',
                                placeItems: 'center',
                                fontSize: 16,
                                flexShrink: 0,
                            }}
                        >
                            S
                        </div>
                        {!collapsed && (
                            <span>
                                SOHO<span style={{ color: '#1677ff' }}>CARD</span>
                            </span>
                        )}
                    </div>
                    {menu}
                </Sider>
            )}

            {isMobile && (
                <Sider
                    theme="dark"
                    width={240}
                    collapsedWidth={0}
                    collapsed={!mobileOpen}
                    trigger={null}
                    style={{
                        position: 'fixed',
                        top: 0,
                        insetInlineStart: 0,
                        height: '100vh',
                        overflow: 'auto',
                        zIndex: 100,
                    }}
                >
                    <div
                        style={{
                            height: 56,
                            margin: '16px 16px 8px',
                            color: '#fff',
                            fontWeight: 800,
                            fontSize: 18,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            letterSpacing: 1,
                        }}
                    >
                        SOHO<span style={{ color: '#1677ff' }}>CARD</span>
                    </div>
                    {menu}
                </Sider>
            )}

            {isMobile && mobileOpen && (
                <div
                    onClick={() => setMobileOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.45)',
                        zIndex: 99,
                    }}
                />
            )}

            <Layout
                style={{
                    marginInlineStart: isMobile ? 0 : collapsed ? 72 : 240,
                    transition: 'margin-inline-start 0.2s',
                }}
            >
                <Header
                    style={{
                        background: '#fff',
                        padding: '0 24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #eaeef5',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                    }}
                >
                    <Space size="middle" align="center">
                        {isMobile && (
                            <Button
                                type="text"
                                icon={<MenuOutlined />}
                                onClick={() => setMobileOpen((v) => !v)}
                                aria-label="Toggle sidebar"
                            />
                        )}
                        <span
                            style={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: 'var(--home-fg, #0b1220)',
                            }}
                        >
                            <FormattedMessage id="admin.dash.title" defaultMessage="Admin console" />
                        </span>
                    </Space>

                    <Space size="large" align="center">
                        <Badge count={0} size="small" showZero={false}>
                            <Button
                                type="text"
                                shape="circle"
                                icon={<BellOutlined style={{ fontSize: 18 }} />}
                                aria-label="Notifications"
                            />
                        </Badge>

                        <Dropdown menu={userMenu} trigger={['click']} placement="bottomRight">
                            <Space style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: 8 }}>
                                <Avatar
                                    style={{
                                        background: 'linear-gradient(135deg, #1677ff, #4096ff)',
                                        flexShrink: 0,
                                    }}
                                >
                                    {userInitial}
                                </Avatar>
                                {!isMobile && (
                                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                                        {user?.email}
                                    </span>
                                )}
                            </Space>
                        </Dropdown>
                    </Space>
                </Header>

                <Content
                    style={{
                        margin: isMobile ? 12 : 24,
                        padding: 0,
                        minHeight: 'calc(100vh - 140px)',
                    }}
                >
                    <Outlet />
                </Content>
            </Layout>
        </Layout>
    );
}